import { useEffect, useMemo, useState } from 'react';
import { apiService } from '../services/api';
import type { AboutUs, Category, Commitment, Insight, Partner, Sector, Statistics, TeamMember, Work } from '../types/api';
import { HeroPlaceholder, HomeHero } from '../components/ui';
import {
  AboutIntro, Commitments, HomeSkeleton, LatestNews, MissionBand, PartnersStrip, ProjectShowcase, SectorsGrid, ServicesShowcase, TeamSpotlight,
} from '../components/home';
import { heroSlides } from '../components/home/heroSlides';
import { useFirstImageReady } from '../components/home/useImagesReady';
import { readHeroCache, writeHeroCache } from '../components/home/heroCache';
import { useNavData } from '../components/layout/useNavData';

type HomeData = {
  about: AboutUs | null;
  stats: Statistics | null;
  works: Work[];
  commitments: Commitment[];
  sectors: Sector[];
  team: TeamMember[];
  partners: Partner[];
  insights: Insight[];
  categories: Category[];
};

const empty: HomeData = { about: null, stats: null, works: [], commitments: [], sectors: [], team: [], partners: [], insights: [], categories: [] };

/** Réalisations à mettre en avant: celles marquées « à la une », sinon les plus récentes. */
const pickWorks = (all: Work[]): Work[] => {
  const time = (w: Work) => (w.completed_date ? Date.parse(w.completed_date) : 0) || 0;
  const byDate = [...all].sort((a, b) => time(b) - time(a));
  const featured = byDate.filter((w) => w.show_up);
  return featured.length > 0 ? featured : byDate;
};

/** Charge toutes les données d'accueil en parallèle; une section en échec n'empêche pas les autres. */
async function loadHome(): Promise<HomeData> {
  const [about, stats, works, commitments, sectors, team, partners, insights, categories] = await Promise.allSettled([
    apiService.getAboutUs(),
    apiService.getStatistics(),
    apiService.getWorks({ enable: true }),
    apiService.getCommitments(),
    apiService.getSectors(),
    apiService.getTeamMembers(),
    apiService.getPartners(),
    apiService.getInsights(),
    apiService.getCategories(),
  ]);
  const val = <T,>(r: PromiseSettledResult<T>, fallback: T): T => (r.status === 'fulfilled' && r.value ? r.value : fallback);
  return {
    about: val(about, null),
    stats: val(stats, null),
    works: pickWorks(val(works, [])),
    commitments: val(commitments, []),
    sectors: val(sectors, []),
    team: val(team, []),
    partners: val(partners, []),
    insights: val(insights, []),
    categories: val(categories, []),
  };
}

export default function Home() {
  const [data, setData] = useState<HomeData>(empty);
  const [dataLoaded, setDataLoaded] = useState(false);
  // Le hero ne dépend que des services (déjà demandés par le header): il n'attend pas le reste de la page.
  const { services, loaded } = useNavData();

  useEffect(() => {
    let alive = true;
    loadHome().then((d) => { if (alive) { setData(d); setDataLoaded(true); } });
    return () => { alive = false; };
  }, []);

  const live = useMemo(() => heroSlides(services), [services]);
  // Au retour d'un visiteur, on part des slides gardées en local, puis on bascule sur les données à jour.
  const [cached] = useState(readHeroCache);
  const slides = live.length > 0 ? live : cached;
  useEffect(() => { if (live.length > 0) writeHeroCache(live); }, [live]);
  // On lance l'animation seulement quand la première image est prête, sinon elle joue sur un fond vide.
  const heroReady = useFirstImageReady(slides.map((s) => s.image));

  return (
    <div id="content">
      {slides.length > 0 && heroReady ? <HomeHero slides={slides} /> : !loaded || slides.length > 0 ? <HeroPlaceholder /> : null}
      {dataLoaded ? (
        <>
        <AboutIntro about={data.about} stats={data.stats} />
        <MissionBand about={data.about} />
        <ProjectShowcase works={data.works} />
        <ServicesShowcase services={services} />
        <Commitments items={data.commitments} />
        <SectorsGrid sectors={data.sectors} />
        <TeamSpotlight members={data.team} />
        <PartnersStrip partners={data.partners} />
        <LatestNews insights={data.insights} categories={data.categories} />
        </>
      ) : (
        <HomeSkeleton />
      )}
    </div>
  );
}
